import { type ReactElement, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import {
  Box,
  Checkbox,
  Flex,
  HStack,
  Text,
} from '@enechain/ecloud-designsystem'

import { HelpIcon } from '~/components/common/HelpIcon'
import {
  POSITION_AXIS_COLOR,
  POSITION_GC_COLOR,
  POSITION_GRID_COLOR,
  POSITION_INTRADAY_COLORS,
  POSITION_SPOT_COLORS,
  POSITION_UNKNOWN_ISSUER_COLOR,
} from '~/constants/chartPalette'
import { komaNumberToTime } from '~/models/komaToTime'

import {
  PositionTooltip,
  resolveCoverageStateKey,
  resolveTooltipTranslateY,
} from './PositionTooltip'
import { resolveRemainMw } from './coverageColors'
import {
  collectVisibleYValues,
  computePositionYAxis,
} from './positionChartAxis'
import type {
  ChartDatum,
  PositionChartColors,
  PositionChartSlot,
} from './types'

// 既存 import path (`~/components/bidding/common/PositionChart`) を壊さないための
// re-export。 実体は `PositionTooltip.tsx` に切り出した (1000 行境界対応)。
export { PositionTooltip, resolveCoverageStateKey, resolveTooltipTranslateY }
export type { ChartDatum, PositionChartColors }

// ----- 色 (= eclair design tokens、 design/css/tokens.css と一致) -----
// ecloud SemanticColors に該当が無い値は chartPalette.ts に集約 (D-1)。
//
// 系列色 (bar / need / cover) は market ごとに design の出典が異なるため palette として
// 切り出し、 colors prop で usage 側が選ぶ (型は tooltip と共有するため `./types` の
// `PositionChartColors` が SoT):
//   - spot.html `spotPosSeries`   : --chart-2/3/1 (= SPOT_COLORS、 default)
//   - intraday.html `intraPosSeries`: --text-positive-mid / --text-warning-high / --text-accent-primary
//     (= INTRADAY_POSITION_COLORS、 時間前市場が渡す)

// spot.html `spotPosSeries` 準拠 (= --chart-2/3/1)。 colors 未指定時の default。
const SPOT_COLORS: PositionChartColors = POSITION_SPOT_COLORS

// intraday.html `intraPosSeries` 準拠。 spot と違い semantic text token を使う
// (= 濃緑 / 濃橙 / 濃青)。 時間前市場 (BidCreateTab) から colors prop で渡す。
export const INTRADAY_POSITION_COLORS: PositionChartColors =
  POSITION_INTRADAY_COLORS

const COLOR_GRID = POSITION_GRID_COLOR
const COLOR_AXIS = POSITION_AXIS_COLOR
// GC reference line。 design/bidding/intraday.html:1576 が token ではなく raw red (#dc2626) を
// 直指定しているための恒久例外 [DS差異] (D-3 監査で design ソースを確認済み、token 化しない)。
const COLOR_GC = POSITION_GC_COLOR

// 凡例 checkbox / 表示状態の key。 `bar` は市場ごとの単一 bar 系列で、 描く値
// (spot=executed / 時間前市場=remain) は `colors.bar` が決める (#8264 review)。
// `executedNormal` / `executedBlock` はスポットの通常/ブロック分割描画 (AC-BSP-016) 専用。
type SeriesKey =
  | 'bar'
  | 'executedNormal'
  | 'executedBlock'
  | 'need'
  | 'cover'
  | 'coverSubmitted'
  | 'unknownIssuer'

type SeriesHint = { content: string; ariaLabel: string }

type SeriesDef = {
  key: SeriesKey
  label: string
  color: string
  hint?: SeriesHint
}

/**
 * 各系列の表示可否を決める (= `PositionChart` 本体の `effectiveVisible` と同一ロジック)。
 *
 * - **凡例に出ていない系列は描かない** (`legendKeys` = `buildSeries` が選んだ key)。
 *   分割描画 (spot) なら `bar` が、 単一 bar なら `executedNormal`/`executedBlock` が
 *   凡例に無く、 ユーザーが操作できない `visible` の初期値 (true) で描かれたり
 *   Y 軸範囲に混ざったりしないようにする。
 * - **入札 0 件 (isEmpty)**: spot の約定 (executed / 通常 / ブロック) と cover は入札が
 *   無ければ実績自体が存在しないため隠す (design `renderSpotPosition` の入札前 view 準拠)。
 *   一方、 時間前市場の remain (入札後残量 = need − cover) は入札 0 件でも
 *   `remain = need − 0 = need` として常に意味を持つ値であり、 AC-BIN-012 は
 *   「48コマのバーチャート（入札時カバー量/調達必要量/入札後残量）を表示」 を 0 件時の
 *   例外なく要求するため、 remain は isEmpty だけでは隠さない (codex 指摘 #748d9a2e7384)。
 * - **need 取得不可 (needUnavailable)**: need line を隠す。 remain (= need − cover) も
 *   need 由来で信頼できないが、 chart と tooltip の扱いを揃えるため
 *   {@link buildPositionChartData} が remain を undefined にする 1 か所で判断する
 *   (ここでは隠さない。 #8264 review)。
 */
export function resolveEffectiveVisible(
  visible: Record<SeriesKey, boolean>,
  legendKeys: readonly SeriesKey[],
  barKey: 'executed' | 'remain',
  isEmpty: boolean,
  needUnavailable: boolean,
): Record<SeriesKey, boolean> {
  const shown = (key: SeriesKey): boolean =>
    legendKeys.includes(key) && visible[key]
  const hideExecutedOnEmpty = isEmpty && barKey === 'executed'
  return {
    bar: shown('bar') && !hideExecutedOnEmpty,
    executedNormal: shown('executedNormal') && !hideExecutedOnEmpty,
    executedBlock: shown('executedBlock') && !hideExecutedOnEmpty,
    need: shown('need') && !needUnavailable,
    cover: shown('cover') && !isEmpty,
    coverSubmitted: shown('coverSubmitted') && !isEmpty,
    unknownIssuer: shown('unknownIssuer'),
  }
}

// design HTML `spot.html` `spotPosSeries` / `intraday.html` `intraPosSeries` と同一構成。
// 凡例の表示順は市場ごとに spec / design の記載順へ合わせる:
//   - spot (分割描画、 `colors.executedBlock` 指定時): 約定(通常) / 約定(ブロック) /
//     調達必要量 / 入札後 (= design `spot.html:3697` spotPosSeries の 4 系列、 AC-BSP-016)
//   - spot (単一 executed bar): 約定後 / 調達必要量 / 入札後
//   - 時間前市場 (remain 指定時): 入札時カバー量 / 調達必要量 / 入札後残量
//     (= AC-BIN-012 の記載順「入札時カバー量/調達必要量/入札後残量」)
// 色は colors prop 由来。 label は i18n 化のため呼び出し側 (component) で `t()` 解決した
// `labels` を受け取る (module-level では `t` を束縛できないため)。
// 線 1・線 2 の違いは凡例の [i] で補足する (design `spot.html` の `legendHints`・
// `intraday.html` の `intraLegendHints`。 スポット・時間前とも付ける)。
const buildSeries = (
  colors: PositionChartColors,
  labels: Record<Exclude<SeriesKey, 'unknownIssuer'>, string>,
  coverHints: { coverSubmitted: SeriesHint; cover: SeriesHint },
): SeriesDef[] => {
  if (colors.bar === 'remain') {
    return [
      {
        key: 'coverSubmitted',
        label: labels.coverSubmitted,
        color: colors.coverSubmitted,
        hint: coverHints.coverSubmitted,
      },
      {
        key: 'cover',
        label: labels.cover,
        color: colors.cover,
        hint: coverHints.cover,
      },
      { key: 'need', label: labels.need, color: colors.need },
      { key: 'bar', label: labels.bar, color: colors.barColor },
    ]
  }
  if (colors.executedBlock !== undefined) {
    return [
      {
        key: 'executedNormal',
        label: labels.executedNormal,
        color: colors.barColor,
      },
      {
        key: 'executedBlock',
        label: labels.executedBlock,
        color: colors.executedBlock,
      },
      { key: 'need', label: labels.need, color: colors.need },
      {
        key: 'coverSubmitted',
        label: labels.coverSubmitted,
        color: colors.coverSubmitted,
        hint: coverHints.coverSubmitted,
      },
      {
        key: 'cover',
        label: labels.cover,
        color: colors.cover,
        hint: coverHints.cover,
      },
    ]
  }
  return [
    { key: 'bar', label: labels.bar, color: colors.barColor },
    { key: 'need', label: labels.need, color: colors.need },
    {
      key: 'coverSubmitted',
      label: labels.coverSubmitted,
      color: colors.coverSubmitted,
      hint: coverHints.coverSubmitted,
    },
    {
      key: 'cover',
      label: labels.cover,
      color: colors.cover,
      hint: coverHints.cover,
    },
  ]
}

// 3 時間刻みの X 軸 tick (= design `_xTicks`、 8080 `bidding/spot.html` L3894)。
// dataKey="time" の category 値で指定する。
// 2 時間刻み (12 個) では狭幅時にラベルが密で読みにくい、 というドッグフーディング
// 指摘 (#5368) を受けて design 側が 3 時間刻み (8 個) へ変更済み。 需要想定側も
// 同様に 0:00 起点 3 時間毎へ是正済み (#5059)。
const X_TICKS = [
  '00:00',
  '03:00',
  '06:00',
  '09:00',
  '12:00',
  '15:00',
  '18:00',
  '21:00',
]

/**
 * 過剰入札量 MW (= need と同方向に |need| を超えた分)。 過剰でなければ undefined。
 * spec AC-BSP-016b / AC-BIN-012a の「超過量 = 入札後カバー量 − 調達必要量」 を
 * need<0 (余剰コマの売り超過) にも効かせるため絶対値の差で算出する。
 */
export function resolveOverMw(
  coverMw: number,
  needMw: number,
): number | undefined {
  if (needMw === 0) return undefined
  if (Math.sign(coverMw) !== Math.sign(needMw)) return undefined
  const over = Math.abs(coverMw) - Math.abs(needMw)
  return over > 0 ? over : undefined
}

type LineDotProps = {
  cx?: number
  cy?: number
  index?: number
  payload?: ChartDatum
}

/**
 * 区分不明の札の線は stepAfter で札のあるコマと直後のコマを結ぶ。 最終コマだけに量がある時は
 * 直後のコマが無く線分が成立しないので、 その点だけを点で描く (#10084)。
 */
const renderUnknownIssuerLoneEnd = ({
  cx,
  cy,
  index,
  payload,
}: LineDotProps): ReactElement =>
  payload?.unknownIssuerLoneEnd === true &&
  cx !== undefined &&
  cy !== undefined ? (
    <circle
      key={`unknown-issuer-dot-${index}`}
      fill={POSITION_UNKNOWN_ISSUER_COLOR}
      cx={cx}
      cy={cy}
      r={3}
    />
  ) : (
    <g key={`unknown-issuer-dot-${index}`} />
  )

/**
 * このコマに札が無いか (AC-BIN-105 / AC-BSP-108)。 買・売の数量がともに 0 なら true。
 * 買いと売りが相殺して net の入札時カバー量が 0 になるコマを「札なし」と取り違えないよう、
 * 方向別の数量で見る。
 */
const isKomaEmpty = (buyMw: number, sellMw: number): boolean =>
  buyMw === 0 && sellMw === 0

/**
 * 48 コマ slot からチャート描画用データを構築する (= 充足率 / 超過量の算出)。
 * 純関数として切り出す理由: happy-dom は `<ResponsiveContainer>` の
 * width/height を 0 にするため recharts の `<Line>`/`<Bar>` が実際には DOM 描画されず、
 * tooltip も (実 hover 座標計算に依存するため) synthetic event では再現できない
 * (= `src/pages/DemandForecastDaily/components/ForecastChart.test.tsx` 冒頭コメントと
 * 同じ制約)。 このため本関数を export し、 データ変換ロジックを DOM 描画を介さず直接
 * ユニットテストできるようにする。
 *
 * slot の cover / executed は net 充当 (買 − 売) で渡される (`PositionChartSlot` / #2533)。
 * spec AC-BSP-016b / AC-BIN-012a (100% クランプ廃止、 issue #3230 → #4570) に従い、
 * ここでは |need| クランプを行わず net をそのまま描画する:
 *
 *   - 過剰入札 (need と同方向に need 超) は need 線を越えて伸び、 充足率は 208% 等の実値
 *   - 逆方向入札 (need と逆符号の net) は 0 で下げ止めず、 充足率はマイナスの実値
 *
 * 充足率は cover (入札後) 基準で算出する (= GC 前・約定前でも自ポジションの過不足が見える)。
 *
 * need<=0 (= 売れる余剰・ロング、 ChartDatum.isSurplus 参照) のコマは 0%→「不足」 表示の
 * 対象から外す (ADR `docs/adr/bidding-position-need-bilateral-baseline.md` の need 符号規約)。
 *
 * need 取得不可 (needUnavailable) 時は need 由来の overMw / remain を undefined にする
 * (need=0 埋めで remain = −cover になり過剰入札に見えるため)。 chart (bar) と tooltip の
 * 両方がこの値に従う (#8264 review)。
 */
export function buildPositionChartData(
  slots: PositionChartSlot[],
  needUnavailable: boolean,
  needAbsent = false,
): ChartDatum[] {
  return slots.map((s, i) => {
    // need 取得不可 (needUnavailable) 時は全コマ need=0 埋めなので、 既存の不足表示のまま
    // 変えないよう isSurplus から除外する。
    const isSurplus = !needUnavailable && s.needMw <= 0
    const coveragePct =
      s.needMw !== 0 ? Math.round((s.coverMw / s.needMw) * 100) : undefined
    const coverageSubmittedPct =
      s.needMw !== 0
        ? Math.round((s.coverSubmittedMw / s.needMw) * 100)
        : undefined
    const unknownIssuerMw = s.unknownIssuerMw ?? 0
    const prevUnknownIssuerMw = slots[i - 1]?.unknownIssuerMw ?? 0
    return {
      index: s.index,
      time: komaNumberToTime(s.index),
      need: s.needMw,
      needAbsent,
      cover: s.coverMw,
      coverSubmitted: s.coverSubmittedMw,
      executed: s.executedMw,
      remain: needUnavailable ? undefined : s.remainMw,
      remainSubmitted: needUnavailable
        ? undefined
        : resolveRemainMw(s.coverSubmittedMw, s.needMw),
      // スポットの通常/ブロック分割描画 (AC-BSP-016) 専用。 時間前市場は slot に
      // これらのフィールドが無いため undefined のまま。
      executedNormal: s.executedNormalMw,
      executedBlock: s.executedBlockMw,
      unknownIssuer: s.unknownIssuerMw,
      unknownIssuerLine:
        unknownIssuerMw !== 0 || prevUnknownIssuerMw !== 0
          ? unknownIssuerMw
          : null,
      unknownIssuerLoneEnd:
        i === slots.length - 1 &&
        unknownIssuerMw !== 0 &&
        prevUnknownIssuerMw === 0,
      coveragePct,
      coverageSubmittedPct,
      overMw: needUnavailable ? undefined : resolveOverMw(s.coverMw, s.needMw),
      overSubmittedMw: needUnavailable
        ? undefined
        : resolveOverMw(s.coverSubmittedMw, s.needMw),
      isEmptyAll: isKomaEmpty(s.buyMw, s.sellMw),
      isEmptySubmitted: isKomaEmpty(s.buySubmittedMw, s.sellSubmittedMw),
      isSurplus,
    }
  })
}

type Props = {
  /** 48 コマ分の slot 配列 (= executed / need / cover / remain の値を含む)。 */
  slots: PositionChartSlot[]
  /** GC 該当 slot index。 ReferenceLine で破線表示 (= 時間前市場用)。 */
  gcSlotIndex?: number
  /**
   * 入札締切の境界 slot index (= これ未満のコマは締切後 → tooltip をクリック不可表示にする)。
   * 未指定なら {@link Props.gcSlotIndex} を使う。 受渡日を選べる時間前市場では、 基準線を引く
   * コマ (= 当日のみ) と締切境界 (= 過去日は全コマ締切済) が一致しないため別 prop で受け取る。
   */
  gcBoundarySlotIndex?: number
  /** chart 高さ (px)。 default 220 (= 8080 `#chart-spot-position` / `#intraday-chart-position` h:220px、 #9626)。 */
  height?: number
  /**
   * コマ (= bar / plot area) クリック時に slot index (1-48) を返す。
   * design HTML の「クリックして入札追加」 動線で使用 (= 該当コマの不足を札作成 modal に
   * prefill する)。 未指定なら chart はクリック非反応 + tooltip のクリックヒントも出さない。
   */
  onSlotClick?: (slotIndex: number) => void
  /**
   * 系列色 palette。 未指定は spot.html 準拠の {@link SPOT_COLORS}。
   * 時間前市場は intraday.html 準拠の {@link INTRADAY_POSITION_COLORS} を渡す。
   */
  colors?: PositionChartColors
  /**
   * need (調達必要量) RPC が取得エラーのとき true (= 設計書 `bidding-position-need.md` §7
   * 受け入れ基準、 恒久 spec のエラーハンドリング節への反映は別 PR。
   * ADR `docs/adr/bidding-position-need-bilateral-baseline.md` 残論点参照)。
   * need line を非表示にし、 凡例付近に注記を 1 行表示する。 cover / executed
   * (入札 RPC 由来、 need に依存しない) は描画を継続するが、 remain (= need − cover、
   * 時間前市場) は need 由来のため値を持たせない (need=0 埋めによる
   * 「過剰入札に見える」 誤表示を防ぐ、 {@link buildPositionChartData} 参照)。
   */
  needUnavailable?: boolean
  /**
   * 取得は成功したが need が無い (発注主体区分がそのエリアに計画を持たない、 AC-BSP-102)。
   * 0 の need は「過不足なし」 と読まれるため need line と need 由来の値を出さないが、
   * 取得エラーではないので注記は出さない。
   */
  needAbsent?: boolean
}

/**
 * recharts v3 の chart onClick state から slot index (= 1-48) を解決する。
 *
 * recharts v3 の click state (MouseHandlerDataParam) は型が公開されておらず
 * `activePayload` を持たないため、 複数経路でフォールバックして index を解決する:
 *   1. `activePayload[0].payload.index` (= 旧 API 互換、 あれば最優先)
 *   2. `activeTooltipIndex` / `activeIndex` (= data 配列の index) → `data[i].index`
 *   3. `activeLabel` (= X category "HH:MM") → `data.find(time === label).index`
 * いずれも解決できなければ undefined。 chart 本体から magic な cast を分離する。
 */
export function resolveSlotIndexFromRechartsClick(
  state: unknown,
  data: ReadonlyArray<{ index: number; time: string }>,
): number | undefined {
  const s = state as {
    activePayload?: Array<{ payload?: { index?: number } }>
    activeTooltipIndex?: number | string | null
    activeIndex?: number | string | null
    activeLabel?: string | number
  }
  const rawIdx = s.activeTooltipIndex ?? s.activeIndex
  const arrIdx =
    typeof rawIdx === 'number'
      ? rawIdx
      : typeof rawIdx === 'string' && rawIdx !== ''
        ? Number(rawIdx)
        : NaN
  const fromIndex =
    Number.isInteger(arrIdx) && arrIdx >= 0 && arrIdx < data.length
      ? data[arrIdx]?.index
      : undefined
  const fromLabel =
    s.activeLabel !== undefined
      ? data.find((d) => d.time === String(s.activeLabel))?.index
      : undefined
  return s.activePayload?.[0]?.payload?.index ?? fromIndex ?? fromLabel
}

/**
 * 入札後ポジション chart。 design HTML `#chart-spot-position` (spot.html) の
 * 入札後ポジション chart と dot-by-dot で一致させる (spot):
 *
 *   - 約定後カバー量 (executed): 緑 bar (= --chart-2)
 *   - 調達必要量 (need): 橙 stepAfter line (= --chart-3)
 *   - 入札後カバー量 (cover): 青 dashed stepAfter line (= --chart-1)
 *
 * スポット (`colors.executedBlock` を指定、 AC-BSP-016) はさらに約定を 通常/ブロック の
 * 2 本の積み上げ棒 (同一 stackId、 通常=緑 --chart-2 / ブロック=赤紫 --chart-5) に分けて
 * 描く (design `spot.html:4117-4118` 準拠)。
 *
 * 時間前市場は AC-BIN-012 / AC-BIN-012a に従い executed の代わりに入札後残量 (remain) を
 * bar 系列に使う（「約定後カバー量は指標として設けない」、 2026-09-02 用語統一。
 * design `intraday.html` の `intraPosSeries` は executed バーのままで本 AC に未追随・
 * stale なので、 本コンポーネントは design ではなく spec (AC-BIN-012 / AC-BIN-012a) を
 * 正とする）。 need / cover の描画は市場間で変わらない。
 *
 * X 軸は "HH:MM" 時刻ラベル + 3 時間刻み tick (#5368、 design `_xTicks` と同値)、 Y 軸は MW 単軸 (= 旧右軸 / カバー率系列 /
 * 残量系列は design 刷新で廃止)。 cover / executed / remain は net 充当 (買 − 売) をクランプせず
 * そのまま描画する (= 余剰コマは負方向、 過剰は need 線を越える。 buildPositionChartData 参照)。
 * tooltip は専用 UI (= カバー率プログレスバー + 調達必要量 / 入札後 / 約定後・入札後残量 の行 +
 * クリックヒント。 スポットは約定行の下に 通常/ブロック の内訳行も追加)。
 *
 * 親 (= 各入札 tab) はパネルの見出し側に 充足率 badge を別途描画する。
 * 本コンポーネントは凡例 + chart 本体 (= height 220px) を担当。
 *
 * chart 本体の高さはスポット・時間前で共通の 220px (#9626、 8080 enechain-labs/ebalance-prototype#458)。
 * 160px では描画域が狭く Y 軸の目盛りが重なって読めなかった。 呼び出し側で上書きしない。
 */
export const POSITION_CHART_HEIGHT = 220

/** 凡例行の高さ。 空状態とガイドは chart 本体 + 凡例行の高さに揃える */
const POSITION_CHART_LEGEND_HEIGHT = 32

/**
 * chart を描かない時 (slots 0 件・単一エリア以外のガイド) の高さ。
 * chart と差し替わっても周りが上下に動かないよう、 chart 本体 + 凡例行と同じにする。
 */
export const POSITION_CHART_PLACEHOLDER_HEIGHT =
  POSITION_CHART_HEIGHT + POSITION_CHART_LEGEND_HEIGHT

export const PositionChart = ({
  slots,
  gcSlotIndex,
  gcBoundarySlotIndex = gcSlotIndex,
  height = POSITION_CHART_HEIGHT,
  onSlotClick,
  colors = SPOT_COLORS,
  needUnavailable = false,
  needAbsent = false,
}: Props): React.ReactElement => {
  const hidesNeed = needUnavailable || needAbsent
  const { t } = useTranslation()

  // 凡例 + chart 系列 (Bar/Line の name) で使う系列ラベル (= i18n 解決済み)。
  // tooltip 側 (`PositionTooltip.tsx` の QuantityRow) は独立に `t()` で解決しており、
  // ここでは共有しない (= 同じ i18n key を指すが値の受け渡しはない)。
  // 実際に使うのは `buildSeries` が選んだ key だけ。 `bar` の label は描く値
  // (spot=約定後カバー量 / 時間前市場=入札後残量) に合わせる。
  const submittedLabel = t('page:biddingCommon.chart.coverSubmittedLabel')
  // 線 1 の [i] の文は市場で共通で、 線 1 に入る状態の列挙だけが違う (スポットだけ約定待ちがある)。
  // key は check-i18n-keys.sh が静的に抽出できる文字列リテラルで書く。
  const coverSubmittedStates =
    colors.bar === 'remain'
      ? t('page:biddingCommon.chart.coverSubmittedStatesIntraday')
      : t('page:biddingCommon.chart.coverSubmittedStatesSpot')
  const seriesLabels: Record<SeriesKey, string> = {
    bar:
      colors.bar === 'remain'
        ? t('page:biddingCommon.chart.remainLabel')
        : t('page:biddingCommon.chart.executedLabel'),
    executedNormal: t('page:biddingCommon.chart.executedNormalLabel'),
    executedBlock: t('page:biddingCommon.chart.executedBlockLabel'),
    need: t('page:biddingCommon.chart.needLabel'),
    cover: t('page:biddingCommon.chart.coverAllLabel'),
    coverSubmitted: submittedLabel,
    unknownIssuer: t('page:biddingCommon.chart.unknownIssuerLabel'),
  }
  const hasUnknownIssuer = slots.some((s) => s.unknownIssuerMw !== undefined)
  // 凡例 + chart 系列で共有する系列定義 (= colors prop 由来)。
  const series: SeriesDef[] = [
    ...buildSeries(colors, seriesLabels, {
      coverSubmitted: {
        content: t('page:biddingCommon.chart.coverSubmittedHint', {
          states: coverSubmittedStates,
        }),
        ariaLabel: t('page:biddingCommon.chart.coverSubmittedHintLabel', {
          submitted: submittedLabel,
        }),
      },
      cover: {
        content: t('page:biddingCommon.chart.coverAllHint', {
          submitted: submittedLabel,
        }),
        ariaLabel: t('page:biddingCommon.chart.coverAllHintLabel'),
      },
    }),
    ...(hasUnknownIssuer
      ? [
          {
            key: 'unknownIssuer' as const,
            label: seriesLabels.unknownIssuer,
            color: POSITION_UNKNOWN_ISSUER_COLOR,
            hint: {
              content: t('page:biddingCommon.chart.unknownIssuerHint', {
                submitted: submittedLabel,
              }),
              ariaLabel: t('page:biddingCommon.chart.unknownIssuerHintLabel'),
            },
          },
        ]
      : []),
  ]
  // 通常/ブロックの分割描画 (スポット、 AC-BSP-016) のときだけ値が入る
  // (buildSeries と同じ判定基準)。
  const executedBlockColor =
    colors.bar === 'executed' ? colors.executedBlock : undefined

  // 系列 checkbox state (= default 全部 on)。 design `spotPosVisible` と同。
  // 凡例に出ない key の値は resolveEffectiveVisible が無視する。
  // 入札後カバー量は「入札済み」「全札（下書き含む）」 の 2 本 (AC-BIN-105 / AC-BSP-108)。
  const [visible, setVisible] = useState<Record<SeriesKey, boolean>>({
    bar: true,
    executedNormal: true,
    executedBlock: true,
    need: true,
    cover: true,
    coverSubmitted: true,
    unknownIssuer: true,
  })

  // cover / executed は net 充当 (買 − 売) をそのまま描く (クランプ廃止、 #4570)。
  // 詳細は buildPositionChartData の godoc 参照。
  const data = useMemo<ChartDatum[]>(
    () => buildPositionChartData(slots, hidesNeed, needAbsent),
    [slots, hidesNeed, needAbsent],
  )

  // chart 描画領域の DOM 参照。 tooltip の縦位置を実際の可視領域へクランプするために使う
  // (= resolveTooltipTranslateY / resolveClipRange 参照)。
  const chartAreaRef = useRef<HTMLDivElement>(null)

  // 入札 0 件判定 (= 全コマで買・売とも 0)。 線の描画の出し分けに使う。 0件は need のみ描画。
  // tooltip の「—」判定はコマごとに行う (`ChartDatum.isEmptyAll` / `isEmptySubmitted`)。
  const isEmpty = useMemo(() => data.every((d) => d.isEmptyAll), [data])

  if (slots.length === 0) {
    return (
      <Flex
        alignItems="center"
        justifyContent="center"
        height={`${height + POSITION_CHART_LEGEND_HEIGHT}px`}
        color="Text.lowEmphasis"
        borderRadius="md"
        backgroundColor="Surface.secondary"
      >
        <Text>{t('page:biddingCommon.chart.empty')}</Text>
      </Flex>
    )
  }

  const toggle = (key: SeriesKey): void => {
    setVisible((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  // 0件時は need (+ 時間前市場の remain) 以外を描画しない
  // (= design `renderSpotPosition` の入札前 view。 remain は AC-BIN-012 により
  // 0 件でも隠さない)。 need 取得不可 (= needUnavailable、 前掲 Props 参照) 時は
  // need line を非表示にし (remain は buildPositionChartData が値を持たせない)、
  // cover / executed (入札 RPC 由来、 need に依存しない) は描画継続する。 凡例に出ない
  // 系列は描かない。 詳細は {@link resolveEffectiveVisible} 参照。
  const effectiveVisible = resolveEffectiveVisible(
    visible,
    series.map((s) => s.key),
    colors.bar,
    isEmpty,
    hidesNeed,
  )

  // Y 軸は 0 を必ず目盛り線に乗せる (= 逆方向入札 / 余剰コマの負値が出ても 0 の位置が読める、
  // issue #5209)。 recharts 既定の domain=[0,'auto'] は 0 が目盛りに乗る保証が無い。
  // 表示中の系列だけを範囲に含める (= 凡例で隠したら軸も縮む)。 算出仕様は positionChartAxis 参照。
  const yAxis = computePositionYAxis(
    collectVisibleYValues(data, effectiveVisible, colors.bar),
  )

  return (
    // need line を描いているかを、 Recharts の描画 (class・色) に依らず観測できるようにする。
    <Box
      data-need-line={effectiveVisible.need || undefined}
      data-testid="position-chart"
    >
      {/* 凡例: chart 上部に center 配置し、 幅が足りなければ折り返す
          (= design `spot-position-legend-slot` の `flex-wrap:wrap; gap: var(--space-1) 14px`)。
          線 1 の名前はスポットで en 40 字と長い。 */}
      <HStack
        justifyContent="center"
        flexWrap="wrap"
        rowGap={1}
        columnGap="14px"
        marginBottom={1}
      >
        {series.map((s) => (
          <HStack key={s.key} gap="2px">
            <Checkbox
              checked={visible[s.key]}
              css={{
                '& .chakra-checkbox__control[data-state="checked"]': {
                  backgroundColor: s.color,
                  borderColor: s.color,
                },
              }}
              onCheckedChange={() => toggle(s.key)}
            >
              {/* 凡例 checkbox のラベルは 14px (デザイナー指示 2026-08-31
                  「入札後のポジション > checkbox の label は 14px、通常ブロック両方」)。
                  DS Checkbox の既定は 12px で、 8080 の `.ecl-checkbox` (= 本文と同じ
                  `--font-size-md` 14px) より小さかった。

                  本 component はスポットの 通常入札 / ブロック入札 と **時間前入札**の
                  3 箇所で共用しているので、 この 1 箇所で 3 画面とも揃う。 */}
              <Text fontSize="md" whiteSpace="nowrap">
                {s.label}
              </Text>
            </Checkbox>
            {s.hint !== undefined && (
              <HelpIcon ariaLabel={s.hint.ariaLabel} content={s.hint.content} />
            )}
          </HStack>
        ))}
      </HStack>

      {/* need 取得エラー時の注記 (= needUnavailable、 前掲 Props 参照)。
          need line 非表示だが cover/executed は描画継続。 */}
      {needUnavailable && (
        <Flex justifyContent="center" marginBottom={1}>
          <Text color="Text.midEmphasis" fontSize="sm">
            {t('page:biddingCommon.chart.needUnavailable')}
          </Text>
        </Flex>
      )}

      {/* 本体 chart。 tooltip の縦クランプ基準 (= viewport 座標) を取るため ref を持つ。 */}
      <Box ref={chartAreaRef}>
        <ResponsiveContainer width="100%" height={height}>
          <ComposedChart
            // GC line label を上端に逃がすため、 GC 表示時 (= intraday) は top を広げる
            // (= design spot.html:4068 top:12 / intraday.html:2088 top:24)。
            // top は 5 → 12 に是正 (design は元から 12。 上端の目盛りラベルと 0 基準線に
            // 余白が無く詰まって見えていた、 デザイナー目視 2026-08-17)。
            // bottom は X 軸ラベルを下げた分 (= XAxis tickMargin) を height 側に持たせるので 0。
            margin={{
              top: gcSlotIndex === undefined ? 12 : 24,
              right: 20,
              bottom: 0,
              left: 0,
            }}
            data={data}
            onClick={
              onSlotClick === undefined
                ? undefined
                : (state) => {
                    const koma = resolveSlotIndexFromRechartsClick(state, data)
                    if (typeof koma === 'number') onSlotClick(koma)
                  }
            }
            style={{
              cursor: onSlotClick === undefined ? undefined : 'pointer',
            }}
          >
            <CartesianGrid stroke={COLOR_GRID} strokeDasharray="3 3" />
            <XAxis
              // 既定 (height 30 / tickMargin 2) だと最初の "00:00" が Y 軸の最下段ラベル
              // (負値のみのコマでは "-40" 等) と斜めに隣接して詰まって見える
              // (デザイナー目視 2026-08-17)。 ラベルを 8px 下げ、 その分を height で確保する
              // (= chart 全体の高さ 160px は変えず、 bottom margin 0 と合わせて配分し直す)。
              height={38}
              axisLine={{ stroke: COLOR_AXIS }}
              dataKey="time"
              interval={0}
              tick={{ fontSize: 12 }} // fontSizes.sm
              tickLine={{ stroke: COLOR_AXIS }}
              tickMargin={8}
              ticks={X_TICKS}
            />
            <YAxis
              width={56}
              axisLine={{ stroke: COLOR_AXIS }}
              // domain / ticks は自前算出 (= 0 を必ず目盛りに乗せる、 #5209)。 recharts 既定は
              // 0 が目盛りに乗る保証が無く、 interval 既定 ('preserveEnd') が目盛りを間引いて
              // 間隔が不均等になる (実測 [0,100,200,400] = 上端だけ幅 2 倍、 spec L314 違反)。
              domain={yAxis.domain}
              interval={0}
              label={{
                value: 'MW',
                angle: -90,
                position: 'insideLeft',
                style: { fontSize: 12 }, // fontSizes.sm
              }}
              tick={{ fontSize: 12 }} // fontSizes.sm
              tickLine={{ stroke: COLOR_AXIS }}
              ticks={yAxis.ticks}
            />
            <Tooltip
              allowEscapeViewBox={{ x: false, y: true }}
              // recharts は content 要素を cloneElement して active / label / payload を注入する。
              content={
                <PositionTooltip
                  chartAreaRef={chartAreaRef}
                  colors={colors}
                  gcBoundarySlotIndex={gcBoundarySlotIndex}
                  isEmpty={isEmpty}
                  showHint={onSlotClick !== undefined}
                />
              }
              offset={16}
              wrapperStyle={{ zIndex: 50 }}
            />
            {/* 約定 bar: スポット (executedBlockColor あり) は 通常/ブロック を
                同一 stackId で積み上げる (design `spot.html:4117-4118`、 AC-BSP-016)。
                それ以外は `colors.bar` の単一 bar (spot=executed / 時間前市場=remain)。 */}
            {effectiveVisible.bar && (
              <Bar
                fill={colors.barColor}
                dataKey={colors.bar}
                name={seriesLabels.bar}
              />
            )}
            {effectiveVisible.executedNormal && (
              <Bar
                fill={colors.barColor}
                dataKey="executedNormal"
                name={seriesLabels.executedNormal}
                stackId="exec"
              />
            )}
            {effectiveVisible.executedBlock &&
              executedBlockColor !== undefined && (
                <Bar
                  fill={executedBlockColor}
                  dataKey="executedBlock"
                  name={seriesLabels.executedBlock}
                  stackId="exec"
                />
              )}
            {effectiveVisible.need && (
              <Line
                stroke={colors.need}
                dataKey="need"
                dot={false}
                name={seriesLabels.need}
                strokeWidth={2}
                type="stepAfter"
              />
            )}
            {effectiveVisible.cover && (
              // 「全札（下書き含む）」線 (AC-BIN-105 / AC-BSP-108)。 破線 = まだ出していない札を
              // 含む。 積み上げず、 先に描いて「入札済み」を上に重ねる。
              <Line
                stroke={colors.cover}
                dataKey="cover"
                dot={false}
                name={seriesLabels.cover}
                strokeDasharray="5 3"
                strokeWidth={2}
                type="stepAfter"
              />
            )}
            {effectiveVisible.coverSubmitted && (
              // 線 1 (AC-BIN-105 / AC-BSP-108)。 実線 = 線 1 に入る札だけ
              // (範囲は市場ごとの `CoverLine` の写像で決まる)。
              <Line
                stroke={colors.coverSubmitted}
                dataKey="coverSubmitted"
                dot={false}
                name={seriesLabels.coverSubmitted}
                strokeWidth={2}
                type="stepAfter"
              />
            )}
            {effectiveVisible.unknownIssuer && (
              <Line
                stroke={POSITION_UNKNOWN_ISSUER_COLOR}
                connectNulls={false}
                dataKey="unknownIssuerLine"
                dot={renderUnknownIssuerLoneEnd}
                name={seriesLabels.unknownIssuer}
                strokeDasharray="2 3"
                strokeWidth={2}
                type="stepAfter"
              />
            )}
            {yAxis.hasNegative && (
              // 負値があるときだけ 0 基準線を実線で引く (= 「どこから下が逆方向か」 を線で示す。
              // design spot.html:3949 / intraday.html:2023 と同じ扱い)。 通常時は X 軸そのものが
              // 0 なので引かない (dashed grid と二重になる)。 series の後に置いて前面に出す。
              <ReferenceLine stroke={COLOR_AXIS} strokeWidth={1} y={0} />
            )}
            {gcSlotIndex !== undefined && (
              // design intraday.html: stroke #dc2626 / width 2 / label "GC (HH:MM)" bold。
              <ReferenceLine
                stroke={COLOR_GC}
                label={{
                  value: `GC (${komaNumberToTime(gcSlotIndex)})`,
                  position: 'top',
                  fill: COLOR_GC,
                  fontSize: 11, // fontSizes.xs (~10px)
                  fontWeight: 700,
                }}
                strokeDasharray="4 4"
                strokeWidth={2}
                x={komaNumberToTime(gcSlotIndex)}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  )
}
